import {
  ButtonBase,
  createStyles,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { RequestIndicatorTypes } from 'data-layer-cr/dist/models';
import {
  colors,
  DefaultCard,
  DisplayItem,
  DropDown,
  FormHookResult,
  InstructionsLayout,
  TextField,
} from 'design-system';
import React, { useState } from 'react';
import { HelpCircle } from 'react-feather';

const useStyles = makeStyles(() =>
  createStyles({
    captionBold: {
      fontWeight: 'bold',
    },
    customButtonText: {
      color: colors.bright_turquoise,
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '19px',
    },
    customButton: {
      display: 'inline-block',
    },
  }),
);

export type IdDropdownOptions = {
  key: string;
  types: DisplayItem[];
  label?: string;
};

export type IdInputOptions = {
  key: string;
  label: string;
  errorMessage: string;
  requestingPerson: RequestIndicatorTypes;
  personIsInvalid: boolean;
};

export type InstructionOptions = {
  inputId: string;
  headerTitle: React.ReactNode;
  statusMessage: React.ReactNode;
  contentTitle: React.ReactNode;
  content: React.ReactNode;
  instructions: React.ReactNode;
};

export type IDCardProps = {
  dropdownOptions: IdDropdownOptions;
  inputOptions: IdInputOptions;
  instructionOptions: InstructionOptions;
  getInputProps: FormHookResult;
};

const IdCard = ({
  dropdownOptions: idOptions,
  inputOptions,
  instructionOptions,
  getInputProps,
}: IDCardProps): JSX.Element => {
  const classes = useStyles();
  const [instructionsVisible, toggleInstructionsVisible] =
    useState<boolean>(false);

  // only id types of Natural Person and Legal Person has instructions defined
  const showInstructions = ['N', 'J'].includes(
    getInputProps(idOptions.key).value as string,
  );

  return (
    <Grid item xs={12}>
      {/* Instructions modal */}
      <InstructionsLayout
        id={instructionOptions.inputId}
        data-testid="instruction-modal"
        open={instructionsVisible}
        headerTitle={instructionOptions.headerTitle}
        title={instructionOptions.contentTitle}
        content={instructionOptions.content}
        instructions={instructionOptions.instructions}
        onCloseRequested={(): void => toggleInstructionsVisible(false)}
      />

      {/* Card Details */}
      <DefaultCard title="Tipo de identificación">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <DropDown
              inputId="sales-web-inspection-dropdown-idtype"
              inputName="idtype-dropdown"
              inputLabel={idOptions.label}
              defaultValue={getInputProps(idOptions.key).value as string}
              options={idOptions.types}
              onChange={(newVal): void => {
                getInputProps(idOptions.key).onChange(newVal.Value);
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              inputId="sales-web-inspection-input-idNumber"
              inputName="idNumber"
              inputLabel={inputOptions.label}
              defaultValue={getInputProps(inputOptions.key).value as string}
              onChange={(value) =>
                getInputProps(inputOptions.key).onChange(value.toUpperCase())
              }
              loading={inputOptions.requestingPerson === 'requesting'}
              inputError={
                inputOptions.personIsInvalid ||
                getInputProps(inputOptions.key).error
              }
              inputErrorMessage={getInputProps(inputOptions.key).errorText}
              helperText={
                (getInputProps(idOptions.key).value as string) === 'U'
                  ? 'Debe iniciar con 1 y contener 12 caracteres'
                  : 'Sin espacios ni guiones (ej. 888888888)'
              }
            />
          </Grid>
          {showInstructions && (
            <Grid
              item
              xs={12}
              container
              alignItems="center"
              justifyContent="center"
            >
              <ButtonBase
                data-testid="id-instructions-button"
                disableRipple
                className={classes.customButton}
                onClick={(): void => toggleInstructionsVisible(true)}
              >
                <Grid container alignItems="center" justifyContent="center">
                  <Typography className={classes.customButtonText}>
                    {instructionOptions.headerTitle}
                  </Typography>
                  &nbsp;
                  {instructionOptions.headerTitle !== '' && (
                    <HelpCircle color={colors.bright_turquoise} />
                  )}
                </Grid>
              </ButtonBase>
            </Grid>
          )}
        </Grid>
      </DefaultCard>
    </Grid>
  );
};

export default IdCard;
